<template>
  <div id="tutorial">
    <div class="tutorial-content">
      <h1>📚 TUTORIAL</h1>
      
      <div class="tutorial-section" v-if="currentStep === 0">
        <h2>Welkom bij het Tutorial!</h2>
        <p>Dit tutorial leert je hoe je het spel speelt.</p>
        <p>Klik op "Volgende" om door te gaan.</p>
      </div>
      
      <div class="tutorial-section" v-if="currentStep === 1">
        <h2>Beweging</h2>
        <div class="controls-list">
          <div class="control-item">
            <span class="key">W</span>
            <span>Vooruit bewegen</span>
          </div>
          <div class="control-item">
            <span class="key">S</span>
            <span>Achteruit bewegen</span>
          </div>
          <div class="control-item">
            <span class="key">A</span>
            <span>Links bewegen</span>
          </div>
          <div class="control-item">
            <span class="key">D</span>
            <span>Rechts bewegen</span>
          </div>
          <div class="control-item">
            <span class="key">Muis</span>
            <span>Rondkijken</span>
          </div>
        </div>
      </div>
      
      <div class="tutorial-section" v-if="currentStep === 2">
        <h2>Schieten</h2>
        <div class="controls-list">
          <div class="control-item">
            <span class="key">Linker Muisknop</span>
            <span>Schieten</span>
          </div>
          <div class="control-item">
            <span class="key">Spatie</span>
            <span>Schieten (alternatief)</span>
          </div>
          <p class="tip">Richt met je muis en schiet op enemies om ze te doden!</p>
        </div>
      </div>
      
      <div class="tutorial-section" v-if="currentStep === 3">
        <h2>Herladen</h2>
        <div class="controls-list">
          <div class="control-item">
            <span class="key">R</span>
            <span>Herladen</span>
          </div>
          <p class="tip">Druk op R wanneer je ammo laag is om te herladen. Dit duurt 2 seconden.</p>
        </div>
      </div>
      
      <div class="tutorial-section" v-if="currentStep === 4">
        <h2>Overleven</h2>
        <ul class="tips-list">
          <li>Je health wordt getoond in de linkerbenedenhoek</li>
          <li>Als enemies te dichtbij komen, vallen ze je aan</li>
          <li>Je hebt allies die je helpen - Mensen en Elven werken samen, andere rassen hebben allies van hun eigen ras</li>
          <li>Blijf op afstand en schiet enemies neer voordat ze je bereiken</li>
        </ul>
      </div>
      
      <div class="tutorial-section" v-if="currentStep === 5">
        <h2>Rassen</h2>
        <div class="race-info">
          <div class="race-item">
            <strong>👤 Mensen:</strong> Gebalanceerd, harnas en helm, zwaard
          </div>
          <div class="race-item">
            <strong>👹 Orks:</strong> Sterk, groene huid, knuppel
          </div>
          <div class="race-item">
            <strong>🧝 Elven:</strong> Snel, puntige oren, boog en pijl
          </div>
          <div class="race-item">
            <strong>🧌 Trollen:</strong> Zeer sterk maar langzaam, grote bijl
          </div>
        </div>
      </div>
      
      <div class="tutorial-section" v-if="currentStep === 6">
        <h2>Klaar om te spelen!</h2>
        <p>Je weet nu hoe het spel werkt.</p>
        <p>Veel succes in de oorlog!</p>
      </div>
      
      <div class="tutorial-navigation">
        <button 
          v-if="currentStep > 0" 
          @click="previousStep" 
          class="nav-button"
        >
          Vorige
        </button>
        <button 
          v-if="currentStep < totalSteps" 
          @click="nextStep" 
          class="nav-button primary"
        >
          {{ currentStep === totalSteps - 1 ? 'Klaar' : 'Volgende' }}
        </button>
        <button 
          @click="skipTutorial" 
          class="nav-button skip"
        >
          Overslaan
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue';

const emit = defineEmits(['close']);

const currentStep = ref(0);
const totalSteps = 7;

const nextStep = () => {
  if (currentStep.value < totalSteps - 1) {
    currentStep.value++;
  } else {
    closeTutorial();
  }
};

const previousStep = () => {
  if (currentStep.value > 0) {
    currentStep.value--;
  }
};

const skipTutorial = () => {
  closeTutorial();
};

const closeTutorial = () => {
  emit('close');
};
</script>

<style scoped>
#tutorial {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.95);
  z-index: 200;
  display: flex;
  justify-content: center;
  align-items: center;
  pointer-events: all;
}

.tutorial-content {
  background: rgba(20, 20, 20, 0.95);
  padding: 40px;
  border-radius: 10px;
  border: 2px solid #8B0000;
  box-shadow: 0 0 30px rgba(139, 0, 0, 0.5);
  max-width: 600px;
  max-height: 80vh;
  overflow-y: auto;
  color: #fff;
}

#tutorial h1 {
  color: #8B0000;
  margin-bottom: 30px;
  font-size: 2.5em;
  text-align: center;
  text-shadow: 2px 2px 4px #000;
}

.tutorial-section {
  min-height: 300px;
}

.tutorial-section h2 {
  color: #ffaa00;
  margin-bottom: 20px;
  font-size: 1.8em;
  border-bottom: 2px solid #8B0000;
  padding-bottom: 10px;
}

.tutorial-section p {
  margin: 15px 0;
  line-height: 1.6;
  font-size: 1.1em;
}

.controls-list {
  margin: 20px 0;
}

.control-item {
  display: flex;
  align-items: center;
  margin: 15px 0;
  padding: 10px;
  background: rgba(139, 0, 0, 0.2);
  border-radius: 5px;
}

.key {
  display: inline-block;
  min-width: 120px;
  padding: 8px 15px;
  background: #333;
  border: 2px solid #666;
  border-radius: 5px;
  font-weight: bold;
  color: #ffaa00;
  text-align: center;
  margin-right: 20px;
}

.tip {
  margin-top: 20px;
  padding: 15px;
  background: rgba(255, 170, 0, 0.1);
  border-left: 4px solid #ffaa00;
  border-radius: 5px;
  font-style: italic;
}

.tips-list {
  list-style: none;
  padding: 0;
}

.tips-list li {
  margin: 15px 0;
  padding: 10px;
  background: rgba(139, 0, 0, 0.2);
  border-radius: 5px;
  padding-left: 30px;
  position: relative;
}

.tips-list li::before {
  content: '⚔️';
  position: absolute;
  left: 10px;
}

.race-info {
  margin: 20px 0;
}

.race-item {
  margin: 15px 0;
  padding: 15px;
  background: rgba(139, 0, 0, 0.2);
  border-radius: 5px;
  border-left: 4px solid #8B0000;
}

.tutorial-navigation {
  display: flex;
  justify-content: space-between;
  margin-top: 30px;
  gap: 10px;
}

.nav-button {
  padding: 12px 25px;
  background: #333;
  border: 2px solid #666;
  color: #fff;
  cursor: pointer;
  border-radius: 5px;
  font-size: 1em;
  font-weight: bold;
  transition: all 0.3s;
  flex: 1;
}

.nav-button:hover {
  background: #444;
  transform: scale(1.05);
}

.nav-button.primary {
  background: #8B0000;
  border-color: #A00000;
}

.nav-button.primary:hover {
  background: #A00000;
  box-shadow: 0 0 15px rgba(139, 0, 0, 0.7);
}

.nav-button.skip {
  background: #444;
  border-color: #666;
}

.nav-button.skip:hover {
  background: #555;
}
</style>


